'use client';

import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { AnimatedInput } from '@/components/AnimatedInput';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { AnimatedTextarea } from '@/components/AnimatedTextArea';
import { PrismicRichText } from '@prismicio/react';
import { asHTML, isFilled, Content } from '@prismicio/client';
import { sendGTMEvent } from '@next/third-parties/google';
import { logger } from '@/utils/logger';
import { getSubmissionContext } from '@/utils/attribution';
import { usePostHogTracking } from '@/lib/posthog';

type ContactDocument = Content.ContactDocument;

interface ContactSectionProps {
  labels: ContactDocument['data'];
  /** Hide the optional Company field (e.g. the Hero CTA form). Shown by default. */
  hideCompany?: boolean;
  /** Override the submit button label (falls back to labels.label_submit). */
  submitLabel?: string;
  /** Let the submit button fill its container instead of capping at 380px. */
  fullWidthButton?: boolean;
  /** Fires once the lead has been submitted, so a parent can hide its own header copy. */
  onSubmitted?: () => void;
}

export function ContactForm({
  labels,
  hideCompany = false,
  submitLabel,
  fullWidthButton = false,
  onSubmitted,
}: ContactSectionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { trackEvent } = usePostHogTracking();

  const formSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, { message: labels.label_no_name ?? '' }),
        email: z.string().email({ message: labels.label_bad_email ?? '' }),
        phone: z.string().optional(),
        company: z.string().optional(),
        message: z.string().min(1, { message: labels.label_no_message ?? '' }),
        hp: z.string().optional(),
      }),
     
    [labels.label_no_name, labels.label_bad_email, labels.label_no_message]
  );

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = handleSubmit(
    async (data: FormData) => {
      logger.log('Valid form data:', data);
      setIsSubmitting(true);
      try {
        // Where the form was submitted from + how the visitor found us.
        const context = getSubmissionContext();

        // Show success immediately — the team's combined notification (contact
        // details + enrichment) is built server-side in the background, so the
        // prospect never waits for the ~20–30s enrichment.
        setIsSubmitted(true);
        onSubmitted?.();

        sendGTMEvent({
          event: 'generate_lead',
          category: 'Form',
          action: 'Submit',
          label: 'Contact Form',
          value: 1,
          event_id: new Date().getTime().toString(),
          transport_url: undefined,
          non_interaction: true,
          page: context.page,
          referral_type: context.referral_type,
          utm_source: context.utm_source,
          utm_medium: context.utm_medium,
          utm_campaign: context.utm_campaign,
        });

        // PostHog lead event (geoip country is auto-attached server-side).
        trackEvent('lead_submitted', {
          page: context.page,
          referral_type: context.referral_type,
          utm_source: context.utm_source,
          utm_medium: context.utm_medium,
          utm_campaign: context.utm_campaign,
        });

        // LinkedIn conversion tracking
        if (typeof window !== 'undefined' && typeof (window as any).lintrk !== 'undefined') {
          (window as any).lintrk('track', { conversion_id: 23651412 });
          logger.log('[LinkedIn] Contact form submission conversion tracked');
        }

        // Fire-and-forget the full submission: a server route runs spam-check +
        // ~20–30s enrichment, then sends ONE combined email + Slack to the team.
        // The contact details always go out even if enrichment fails/times out.
        // keepalive lets it survive navigation; we intentionally don't await.
        fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({ ...data, ...context }),
        }).catch(() => {});

        toast({
          title: 'Message sent',
          description: "We've received your message and will get back to you soon.",
        });
      } catch (error) {
        logger.error('Error submitting form:', error);
        logger.error('Current field values:', getValues());
        toast({
          title: 'Error',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    (errors) => {
      logger.log('Form validation errors:', errors);
      logger.log('Current field values:', getValues());
      toast({
        title: 'Invalid form',
        description: 'Please fill out all required fields correctly.',
        variant: 'destructive',
      });
    }
  );

  return (
    <form id="contact-form" onSubmit={onSubmit} className="mx-auto w-full" data-ga4-auto="false">
      {!isSubmitted && (
        <fieldset disabled={isSubmitted} className="space-y-6">
          <AnimatedInput
            label={labels.label_name ?? ''}
            {...register('name')}
            className={cn(errors.name && 'border-red-500', 'max-w-[500px]')}
            error={errors.name?.message}
            autoComplete="name"
          />
          <AnimatedInput
            label={labels.label_email ?? ''}
            type="email"
            {...register('email')}
            className={cn(errors.email && 'border-red-500', 'max-w-[500px]')}
            error={errors.email?.message}
            autoComplete="email"
          />
          <AnimatedInput
            label={labels.label_phone ?? ''}
            {...register('phone')}
            className={cn(errors.phone && 'border-red-500', 'max-w-[380px]')}
            error={errors.phone?.message}
            autoComplete="tel"
            type="tel"
          />
          {!hideCompany && (
            <AnimatedInput
              label={(labels as { label_company?: string }).label_company ?? 'Company'}
              {...register('company')}
              className={cn(errors.company && 'border-red-500', 'max-w-[500px]')}
              error={errors.company?.message}
              autoComplete="organization"
            />
          )}
          {/* Honeypot — hidden from real users, bots fill it */}
          <input
            type="text"
            {...register('hp')}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 overflow-hidden opacity-0"
          />
          <div className="relative">
            <AnimatedTextarea
              label={labels.label_message ?? ''}
              {...register('message')}
              className={cn('min-h-[100px]', errors.message && 'border-red-500')}
              error={errors.message?.message}
            />
          </div>
        </fieldset>
      )}
      {!isSubmitted ? (
        <Button
          type="submit"
          className={cn(
            'mt-6 h-12 w-full bg-gradient-to-b from-red-500 to-red-700 text-base font-semibold uppercase hover:opacity-90',
            fullWidthButton ? 'max-w-none' : 'max-w-[380px]'
          )}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? (labels.label_submitting ?? '')
            : (submitLabel || labels.label_submit || '')}
        </Button>
      ) : (
        <div className="mt-10 flex flex-col items-center text-center" aria-live="polite">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-600"
          >
            <motion.svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-11 w-11"
            >
              <motion.path
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
              />
            </motion.svg>
          </motion.div>
          <div
            className="mt-6 text-2xl font-normal text-gray-900 [&_strong]:font-bold [&_strong]:text-blue-700 sm:text-3xl leading-[1.2]"
            dangerouslySetInnerHTML={{ __html: asHTML(labels.label_success) ?? '' }}
          />
          {isFilled.richText(labels.label_success_subtitle) && (
            <div className="mt-3 max-w-sm text-base text-gray-500">
              <PrismicRichText field={labels.label_success_subtitle} />
            </div>
          )}
        </div>
      )}
    </form>
  );
}
